import { useContext } from "react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { ReportsContext } from "../../providers/ReportsProvider";
import AccordionItemsRenderer from "./components/AccordionItemsRenderer";
import { CollapseIcon, Download, ExpandIcon } from "../../assets";
import { AccordianSkeleton } from "../../components/Skeleton/ReportSkeleton";
import { ChevronLeft, ChevronRight } from "lucide-react";
import moment from "moment";
import { CustomMonthPicker } from "../../components/ui/customMonthPicker";
import { EmptyState } from "../../components/ui/empty-state";
import { StringOverrides } from ".";

const ReportsTabs = ({
  stringOverrides,
  withDownloadButton = true,
}: {
  stringOverrides?: StringOverrides;
  withDownloadButton?: boolean;
}) => {
  const {
    data,
    isLoading,
    activeTab,
    isExpanded,
    toggleAccordion,
    date,
    setDate,
    onDateChange,
    handleTabChange,
    expandAllRows,
    collapseAllRows,
    areAllRowsExpanded,
    enabledReports,
    handleDownload,
    isDownloading,
  } = useContext(ReportsContext);

  const handlePreviousMonth = () => {
    const newDate = moment(date).subtract(1, "month").toDate();
    onDateChange(newDate);
  };

  const handleNextMonth = () => {
    const newDate = moment(date).add(1, "month").toDate();
    onDateChange(newDate);
  };

  const isNextMonthDisabled = () => {
    const currentMonth = moment().startOf("month");
    const selectedMonth = moment(date).startOf("month");
    return selectedMonth.isSameOrAfter(currentMonth);
  };

  const handleDateChange = (newDate: Date) => {
    setDate(newDate);
    onDateChange(newDate);
  };

  return (
    <>
      <div className="flex flex-wrap gap-2 md:gap-0 md:flex-nowrap items-center justify-between my-6">
        <Tabs
          defaultValue={activeTab}
          onValueChange={handleTabChange}
          className="w-full flex flex-col"
        >
          <div className="mb-4">
            <div className="flex flex-wrap gap-3 items-center justify-start! md:justify-between!">
              {/* Reports Tabs */}
              <h1 className="BookEZ_font_body_large BookEZ_font_bold">
                {activeTab == "profitAndLoss"
                  ? "Profit & Loss"
                  : "Balance Sheet"}
              </h1>
              <div className="flex flex-wrap lg:flex-nowrap! items-center gap-2">
                {enabledReports && enabledReports.length > 1 && (
                  <TabsList className="h-[42px] p-1 max-w-[300px] BookEZ_font_color_gray_2">
                    {enabledReports.map((report) => (
                      <TabsTrigger
                        key={report.type}
                        value={report.type}
                        className="px-3 md:px-5 h-8 flex-1 md:flex-none"
                      >
                        {report.label}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                )}

                <div className="flex gap-3 flex-wrap md:flex-nowrap! justify-start">
                  {/* Date Controls */}
                  <div className="flex items-center gap-2 w-auto">
                    <div className="flex items-center gap-2 flex-none">
                      <Button
                        onClick={handlePreviousMonth}
                        variant="ghost"
                        size="icon"
                        className="shrink-0"
                      >
                        <ChevronLeft className="h-4 w-4 BookEZ_font_color_gray_2" />
                      </Button>
                      <CustomMonthPicker
                        date={date}
                        setDate={(date) => handleDateChange(date)}
                        trigger={
                          <div className="px-2 md:px-3 py-1.5 border BookEZ_btn_ghost_border BookEZ_font_color_gray_2 BookEZ_btn_radius text-center w-[160px] BookEZ_font_body_size cursor-pointer">
                            {moment(date).format("MMMM YYYY")}
                          </div>
                        }
                      />
                      <Button
                        onClick={handleNextMonth}
                        variant="ghost"
                        size="icon"
                        className="shrink-0"
                        disabled={isNextMonthDisabled()}
                      >
                        <ChevronRight className="h-4 w-4 BookEZ_font_color_gray_2" />
                      </Button>
                    </div>
                  </div>
                  {/* Action Buttons */}
                  <div className="flex items-center gap-2">
                    {areAllRowsExpanded(activeTab) ? (
                      <Button
                        variant="ghost"
                        size="md"
                        onClick={() => collapseAllRows(activeTab)}
                        className="h-10 p-0 w-10"
                      >
                        <CollapseIcon className="h-4 w-4 BookEZ_font_color_gray_2" />
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="md"
                        onClick={() => expandAllRows(activeTab)}
                        className="h-10 w-10 p-0 BookEZ_table_bg_odd_stripe_color"
                      >
                        <ExpandIcon className="h-4 w-4 BookEZ_font_color_gray_2" />
                      </Button>
                    )}
                    {withDownloadButton && (
                      <Button
                        variant="ghost"
                        size="md"
                        className="h-10 p-0 w-10 BookEZ_table_bg_odd_stripe_color"
                        onClick={handleDownload}
                        disabled={
                          isDownloading ||
                          (activeTab === "profitAndLoss"
                            ? data.profitAndLoss.profitData.length === 0
                            : data.balanceSheet.length === 0)
                        }
                      >
                        <Download className="h-4 w-4 BookEZ_font_color_gray_2" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {isLoading ? (
            <AccordianSkeleton />
          ) : (
            <>
              {activeTab === "profitAndLoss" && (
                <Card className="py-3 ">
                  <TabsContent key="profitAndLoss" value="profitAndLoss">
                    {data.profitAndLoss.profitData.length > 0 ? (
                      <div className="">
                        <AccordionItemsRenderer
                          items={data.profitAndLoss.profitData || []}
                          tabId="profitAndLoss"
                          isExpanded={isExpanded}
                          toggleAccordion={toggleAccordion}
                          taxes={data.profitAndLoss.taxes || {}}
                          stringOverrides={stringOverrides}
                        />
                      </div>
                    ) : (
                      <div className="rounded-lg border BookEZ_card_border_color">
                        <EmptyState
                          title="No Data Found"
                          description="There is no data to show you right now"
                        />
                      </div>
                    )}
                  </TabsContent>
                </Card>
              )}
              {activeTab === "balanceSheet" && (
                <Card className="py-3 ">
                  <TabsContent key="balanceSheet" value="balanceSheet">
                    {data.balanceSheet.length > 0 ? (
                      <div className="">
                        <AccordionItemsRenderer
                          items={data.balanceSheet || []}
                          tabId="balanceSheet"
                          isExpanded={isExpanded}
                          toggleAccordion={toggleAccordion}
                        />
                      </div>
                    ) : (
                      <div className="rounded-lg border BookEZ_card_border_color">
                        <EmptyState
                          title="No Data Found"
                          description="There is no data to show you right now"
                        />
                      </div>
                    )}
                  </TabsContent>
                </Card>
              )}
            </>
          )}
        </Tabs>
      </div>
    </>
  );
};

export default ReportsTabs;
